'use client';

import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { getDomain } from '@domain/routes/urls';
import { EventMykissShareClicked } from '@generated/events';
import { ExternalLink } from '../Clickables/ExternalLink/ExternalLink';
import { Heading } from '../Heading/Heading';
import { ShareLinkIcon } from '../Icons/ShareLinkIcon';
import { FacebookBlueIcon } from '../Icons/SocialMediaIcons/FacebookBlueIcon';
import { LinkedInBlueIcon } from '../Icons/SocialMediaIcons/LinkedInBlueIcon';
import { XBlackIcon } from '@components/Icons/SocialMediaIcons/XBlackIcon';
import { ShareButton } from '../ShareButton/ShareButton';
import { Text } from '../Text/Text';

interface IProps {
  /* The path of the url to share, for example `/fishing-water/abc123/some-water` */
  path: string;
  /* A unique id for the location where the social share links exist, e.g. "BlogPost" */
  source: EventMykissShareClicked['properties']['source'];
  /* ID of the source, e.g. the slug of the specific blog post */
  sourceId?: EventMykissShareClicked['properties']['source_id'];
}

export const SocialShareLinks = ({ path, source, sourceId }: IProps) => {
  const { currentPage } = useAnalytics();

  const onClick = (destination: EventMykissShareClicked['properties']['destination']): void => {
    trackEvent({
      name: 'mykiss_share_clicked',
      properties: { page: currentPage, destination, source, source_id: sourceId },
    });
  };

  return (
    <div className="flex flex-col items-center sm:items-start" data-testid="SocialShareLinks">
      <Heading className="md:hidden" variant="2xs">
        Share this article
      </Heading>
      <div className={`not-prose fill-abyss flex gap-4 md:mt-6`}>
        <div className="group relative inline-block cursor-pointer">
          <ExternalLink
            variant="textNone"
            onClick={() => {
              onClick('linkedin');
            }}
            data-testid="linkedin_share"
            target="_blank"
            href={`https://www.linkedin.com/sharing/share-offsite/?url=https://fishbrain.com${path}`}
          >
            <LinkedInBlueIcon width={32} height={32} ariaLabel="Share on LinkedIn" />
          </ExternalLink>
          <Text variant="xs" className="tooltip-text">
            Share on LinkedIn
          </Text>
        </div>
        <div className="group relative inline-block cursor-pointer">
          <ExternalLink
            variant="textNone"
            onClick={() => {
              onClick('facebook');
            }}
            target="_blank"
            data-testid="facebook_share"
            href={`https://www.facebook.com/sharer.php?u=https://fishbrain.com${path}`}
          >
            <FacebookBlueIcon width={32} height={32} ariaLabel="Share on Facebook" />
            <Text variant="xs" className="tooltip-text">
              Share on Facebook
            </Text>
          </ExternalLink>
        </div>
        <div className="group relative inline-block cursor-pointer">
          <ExternalLink
            variant="textNone"
            onClick={() => {
              // Stays 'twitter': the destination comes from the generated tracking plan, so
              // renaming it needs a batrachus-analytics-events change and splits the metric.
              onClick('twitter');
            }}
            target="_blank"
            data-testid="x_share"
            href={`https://x.com/intent/post?url=https://fishbrain.com${path}`}
          >
            <XBlackIcon width={32} height={32} ariaLabel="Share on X" />
          </ExternalLink>
          <Text variant="xs" className="tooltip-text">
            Share on X
          </Text>
        </div>
        <ShareButton
          parentComponent="BlogPost"
          shareData={{ url: `${getDomain()}${path}` }}
          onClick={() => {
            onClick('share-link');
          }}
          buttonHeight={32}
          buttonWidth={32}
        >
          <ShareLinkIcon width={32} height={32} />
        </ShareButton>
      </div>
    </div>
  );
};
